import { Icon } from '@natera/material/lib/icon'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { isNonFinancialEob, useSubcategories } from '~/entities/categories'
import {
  getDocumentStepUrl,
  getDocumentsUrlWithCurrentParams,
  getIsEobProcessingFinished,
  isLastStep,
  useCurrentDocument,
  usePaymentSummary,
  useUpdateDocumentPayor,
  useValidateEobProcessing,
} from '~/entities/document'
import {
  getStepperFlagSeverity,
  hasActiveFlagOnStep,
  useDocumentFlags,
} from '~/entities/document-flags'
import { useAuditTrailQueryParams } from '~/features/audit-trail'
import { useStepCompletion } from '~/features/document-analytics'
import { useEobOnboardingSelectors } from '~/features/eob-onboarding'
import { closePdfTabOrRemind } from '~/features/pdf-tab-reminder'
import { cn } from '~/shared/lib/cn'
import { IconButton, Popover, PopoverAnchor, PopoverContent, Text } from '~/shared/ui'
import { Tooltip } from '~/shared/ui/local-tooltip'
import { ReviewActions } from '~/widgets/review-actions'
import { usePatientClaims } from '../api'
import { PatientClaimData } from './patient-claim-data/patient-claim-data'
import { PaymentSummary } from './payment-summary/payment-summary'

type EobTab = 'payment-summary' | 'patient-claim-data'

/**
 * EOB Processing page content. Renders Payment Summary and Patient Claim Data
 * tabs with a progress tracker and ReviewActions wired to "Validate & Finish".
 */
export function EobProcessingContent() {
  const { trackStepCompletion } = useStepCompletion()
  const { data: flagsData } = useDocumentFlags()
  const hasUnresolvedFlags = hasActiveFlagOnStep(flagsData?.items ?? [], 'eob_processing')
  const { data: summaryData } = usePaymentSummary()
  const { data: claimsData } = usePatientClaims()
  const [activeTab, setActiveTab] = useState<EobTab>('payment-summary')
  const [isScrolledBottom, setIsScrolledBottom] = useState(false)
  const {
    data: { document },
  } = useCurrentDocument()
  const subcategories = useSubcategories()
  const isNonFinancial = isNonFinancialEob(document.category)

  const {
    isSummaryReviewedOnboardingOpen,
    setIsSummaryReviewedOnboardingOpen,
    markSummaryReviewedOnboardingSeen,
  } = useEobOnboardingSelectors()
  const handleDismissSummaryReviewedOnboarding = () => {
    setIsSummaryReviewedOnboardingOpen(false)
    markSummaryReviewedOnboardingSeen()
  }

  const [isSummaryReviewed, setIsSummaryReviewed] = useState(false)
  const [allClaimsReviewed, setAllClaimsReviewed] = useState(false)
  const [isSummaryModified, setIsSummaryModified] = useState(false)
  const [isClaimsModified, setIsClaimsModified] = useState(false)

  const claimFlagSeverity = getStepperFlagSeverity(flagsData?.items ?? [])
  const tabs: {
    id: EobTab
    label: string
    flagSeverity?: 'high' | 'low' | null
    hasUnsavedChanges?: boolean
  }[] = [
    {
      id: 'payment-summary',
      label: isNonFinancial ? 'Document Summary' : 'Payment Summary',
      hasUnsavedChanges: isSummaryModified && activeTab !== 'payment-summary',
    },
    {
      id: 'patient-claim-data',
      label: 'Patient Claim Data',
      flagSeverity: claimFlagSeverity,
      hasUnsavedChanges: isClaimsModified && activeTab !== 'patient-claim-data',
    },
  ]
  const { mutateAsync: updateDocumentPayor, isPending: isPayorUpdatePending } =
    useUpdateDocumentPayor()
  const { mutateAsync: validateEobProcessing, isPending: isValidatePending } =
    useValidateEobProcessing()
  const navigate = useNavigate()
  const queryParams = useAuditTrailQueryParams()

  const totalPaidCents = summaryData.total_paid ? Number(summaryData.total_paid) : 0
  const deductedCents = (summaryData.adjustments ?? []).reduce(
    (acc, adj) => acc + (adj.deduct_from_total_paid && adj.amount ? Number(adj.amount) : 0),
    0,
  )
  const allocatedCents = claimsData.patient_claims.reduce(
    (acc, claim) => acc + (claim.total_paid ? Number(claim.total_paid) : 0),
    0,
  )
  const expectedPatientClaimsNumber = summaryData?.number_of_patient_claims ?? 0
  const validatedPatientClaimsNumber = claimsData.patient_claims.length

  async function handleValidateAndFinish() {
    await validateEobProcessing({
      path: { document_key: document.key },
    })
    trackStepCompletion()
    // Post-server-ack: trigger PDF tab close reminder if we just finished the
    // last step of the EOB flow (financial or non-financial). Fire-and-forget
    // so its resolution never affects validation UX.
    if (
      isLastStep('eob_processing', {
        isTaggingEnabled: false,
        isEob: true,
        isNonFinancialEob: isNonFinancial,
      })
    ) {
      void closePdfTabOrRemind(document.key)
    }
    navigate(getDocumentsUrlWithCurrentParams())
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="border-divider shrink-0 border-b">
        {tabs.map((tab) => {
          const isHighlightedByOnboarding =
            tab.id === 'patient-claim-data' && isSummaryReviewedOnboardingOpen
          const button = (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'text-text-secondary relative inline-block cursor-pointer border-none bg-transparent px-6 py-3 text-sm leading-6 font-medium tracking-[1.25px] uppercase',
                'after:bg-primary-main after:absolute after:-bottom-px after:left-0 after:hidden after:h-0.5 after:w-full after:content-[""]',
                {
                  'text-primary-main after:block': activeTab === tab.id,
                  'bg-action-hovered': isHighlightedByOnboarding,
                },
              )}
            >
              {tab.label}
              {tab.hasUnsavedChanges ? (
                <span
                  aria-hidden
                  className="bg-primary-main absolute top-1/2 right-2 h-2 w-2 -translate-y-1/2 rounded-full"
                />
              ) : (
                tab.flagSeverity && (
                  <Icon
                    className={cn('absolute top-1/2 right-1 -translate-y-1/2 text-[16px]', {
                      'text-alert-warning-message': tab.flagSeverity === 'high',
                      'text-primary-main': tab.flagSeverity === 'low',
                    })}
                    children="flag"
                    fill
                  />
                )
              )}
            </button>
          )
          if (tab.id === 'patient-claim-data') {
            return (
              <Popover
                key={tab.id}
                open={isSummaryReviewedOnboardingOpen}
                onOpenChange={(open) => {
                  if (!open) handleDismissSummaryReviewedOnboarding()
                }}
              >
                <PopoverAnchor asChild>{button}</PopoverAnchor>
                <PopoverContent
                  side="bottom"
                  align="start"
                  sideOffset={1}
                  onOpenAutoFocus={(e) => e.preventDefault()}
                  className="relative flex w-[320px] items-start gap-4 px-4 py-3"
                >
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <Text appearance="SUBTITLE2">Keep Rolling</Text>
                    <Text
                      className="text-text-secondary"
                      appearance="CAPTION"
                    >
                      Keep going! Head to the Patient Claim Data tab and start adding service lines.
                    </Text>
                  </div>
                  <IconButton
                    size="small"
                    container="small"
                    materialIcon="close"
                    onClick={handleDismissSummaryReviewedOnboarding}
                    className="absolute top-2 right-2"
                  />
                </PopoverContent>
              </Popover>
            )
          }
          return tab.hasUnsavedChanges ? (
            <Tooltip
              key={tab.id}
              content="You have unsaved changes on this tab"
              side="top"
            >
              {button}
            </Tooltip>
          ) : (
            button
          )
        })}
      </div>
      <div
        className={cn('flex min-h-0 flex-1', {
          hidden: activeTab !== 'payment-summary',
        })}
      >
        <PaymentSummary
          isNonFinancial={isNonFinancial}
          onScrolledBottomChange={activeTab === 'payment-summary' ? setIsScrolledBottom : undefined}
          onReviewedChange={setIsSummaryReviewed}
          onModifiedChange={setIsSummaryModified}
          documentPayorId={document.payor_id}
          isPayorUpdatePending={isPayorUpdatePending}
          onPayorChanged={async (payorId) => {
            if (payorId !== (document.payor_id ?? '')) {
              await updateDocumentPayor({
                path: { key: document.key },
                body: { payor_id: payorId },
              })
            }
          }}
        />
      </div>
      <div
        className={cn('flex min-h-0 flex-1', {
          hidden: activeTab !== 'patient-claim-data',
        })}
      >
        <PatientClaimData
          claimsData={claimsData}
          checkedPatients={summaryData.checked_patients}
          onScrolledBottomChange={
            activeTab === 'patient-claim-data' ? setIsScrolledBottom : undefined
          }
          onAllReviewedChange={setAllClaimsReviewed}
          onModifiedChange={setIsClaimsModified}
        />
      </div>
      <ReviewActions
        isDirty={false}
        isSubmitDisabled={
          hasUnresolvedFlags
            ? false
            : isValidatePending ||
              !isSummaryReviewed ||
              !allClaimsReviewed ||
              (!isNonFinancial && totalPaidCents + deductedCents !== allocatedCents) ||
              (!!expectedPatientClaimsNumber &&
                validatedPatientClaimsNumber !== expectedPatientClaimsNumber) ||
              getIsEobProcessingFinished(document, subcategories)
        }
        onSubmit={
          hasUnresolvedFlags
            ? () => navigate(getDocumentsUrlWithCurrentParams())
            : handleValidateAndFinish
        }
        submitLabel={hasUnresolvedFlags ? 'Return to Work Queue' : 'Validate & Finish'}
        isScrolled={isScrolledBottom}
        previousStepUrl={getDocumentStepUrl(document.key, 'eob_duplication', { queryParams })}
        previousStepLabel="Go to Duplication"
      />
    </div>
  )
}
